import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DetectMethodChanges } from '../utils';
import { TabKind } from './opg-copilot-domain';

// The three-tab switcher: Calls | Puts | Calls & Puts (spec §5). Dumb/presentational —
// emits the chosen TabKind; the orchestrator owns the active state and locks switching
// while a run is in flight (single-flight) via [disabled].
//
// ISOLATED SIBLING of the APG/Hedge CoPilot tab bars (family rule — duplication, never
// cross-folder imports): the SAME control, a full-width contained dx-button-group whose
// selected segment is recolored to the CoPilot accent by `copilot-segmented-selected`.
// The button group spans the FULL dialog width (owner ruling 2026-07-10 — all segmented
// groups full width).
@Component({
    selector: 'ets-opg-copilot-tab-bar',
    templateUrl: './opg-copilot-tab-bar.component.html',
    styleUrls: ['./opg-copilot-tab-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgCopilotTabBarComponent {
    constructor(private _changeDetector: ChangeDetectorRef) {
    }

    // Plain input, not a setter: the emit lives ONLY in the user-driven click handler
    // below (never from the setter — echo-loop hazard). Supports [(activeTab)].
    @Input() activeTab: TabKind;
    @Output() activeTabChange = new EventEmitter<TabKind>();

    // true while a run is in flight — tab switching is locked for the duration.
    @Input() disabled: boolean;

    // CR-20260831-004 round 3 (owner recording): the ACTIVE tab is not the tab of the LAST run —
    // "what is selected now is not what has been ran, the results you are looking at". Paints the
    // selected segment yellow (the same param-changed treatment every mode selector uses).
    @Input() notLastRunTab = false;

    // Labels EXACT (sketch frame 02): "Calls", "Puts", "Calls & Puts". Per-item
    // data-element ids from the sketch starter set ride in via elementAttr.
    readonly tabItems: { id: TabKind; text: string; elementAttr: { [key: string]: string } }[] = [
        { id: 'calls', text: 'Calls', elementAttr: { 'data-element': 'tab-calls' } },
        { id: 'puts', text: 'Puts', elementAttr: { 'data-element': 'tab-puts' } },
        { id: 'callsAndPuts', text: 'Calls & Puts', elementAttr: { 'data-element': 'tab-calls-puts' } }
    ];

    // dx-button-group fires onItemClick for user clicks only (programmatic
    // [selectedItemKeys] changes do not re-enter here), so no $event.event guard needed.
    @DetectMethodChanges()
    onItemClick(e: { itemData: { id: TabKind } }): void {
        this.activeTabChange.emit(e.itemData.id);
    }
}
